import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { decodeId } from '@app/helpers/base64url';

/**
 * Decodes an obfuscated numeric ID route param.
 * If the token is missing or cannot be decoded to a valid positive integer,
 * shows a generic error toast and redirects to `fallbackPath`.
 *
 * Usage:
 *   const id = useDecodedParam('id', '/students');
 *   const id = useDecodedParam('encodedId', '/centers');
 *
 * Returns the decoded number, or 0 while redirect is pending.
 */
export function useDecodedParam(name: string, fallbackPath: string = '/'): number {
    const params = useParams();
    const navigate = useNavigate();
    const id = decodeId(params[name]);
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        if (id === null || id <= 0 || isNaN(id)) {
            setIsRedirecting(true);
            toast.error('This link is invalid or has expired.');
            // Use setTimeout to ensure state updates before navigation
            setTimeout(() => {
                navigate(fallbackPath, { replace: true });
            }, 0);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Return 0 immediately if invalid ID to prevent API calls
    if (id === null || id <= 0 || isNaN(id) || isRedirecting) {
        return 0;
    }

    return id;
}
